import { useState, useEffect, useCallback } from "react"
import { useAuth } from "@/hooks/useAuth"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import {
  listGroups,
  createGroup,
  updateGroup,
  deleteGroup,
  listGroupMembers,
  addGroupMember,
  removeGroupMember,
  type Group,
  type GroupMember,
} from "@/services/groupService"
import { listUsers, type User } from "@/services/adminService"
import { Plus, Pencil, Trash2, UserPlus, X } from "lucide-react"

export default function GroupManagement() {
  const { token } = useAuth()
  const [groups, setGroups] = useState<Group[]>([])
  const [loading, setLoading] = useState(true)

  // Group create/edit dialog
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingGroup, setEditingGroup] = useState<Group | null>(null)
  const [groupName, setGroupName] = useState("")
  const [groupDesc, setGroupDesc] = useState("")
  const [saving, setSaving] = useState(false)

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState<Group | null>(null)

  // Members panel
  const [selectedGroup, setSelectedGroup] = useState<Group | null>(null)
  const [members, setMembers] = useState<GroupMember[]>([])
  const [membersLoading, setMembersLoading] = useState(false)
  const [addMemberOpen, setAddMemberOpen] = useState(false)
  const [users, setUsers] = useState<User[]>([])
  const [selectedUserId, setSelectedUserId] = useState("")

  const loadGroups = useCallback(async () => {
    if (!token) return
    setLoading(true)
    try {
      setGroups(await listGroups(token))
    } catch (e) {
      console.error("Failed to load groups:", e)
    } finally {
      setLoading(false)
    }
  }, [token])

  useEffect(() => {
    loadGroups()
  }, [loadGroups])

  const loadMembers = useCallback(
    async (groupId: string) => {
      if (!token) return
      setMembersLoading(true)
      try {
        setMembers(await listGroupMembers(token, groupId))
      } catch (e) {
        console.error("Failed to load members:", e)
      } finally {
        setMembersLoading(false)
      }
    },
    [token]
  )

  const openCreate = () => {
    setEditingGroup(null)
    setGroupName("")
    setGroupDesc("")
    setDialogOpen(true)
  }

  const openEdit = (group: Group) => {
    setEditingGroup(group)
    setGroupName(group.name)
    setGroupDesc(group.description)
    setDialogOpen(true)
  }

  const handleSave = async () => {
    if (!token || !groupName.trim()) return
    setSaving(true)
    try {
      if (editingGroup) {
        await updateGroup(token, editingGroup.groupId, groupName, groupDesc)
      } else {
        await createGroup(token, groupName, groupDesc)
      }
      setDialogOpen(false)
      await loadGroups()
    } catch (e) {
      console.error("Failed to save group:", e)
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!token || !deleteTarget) return
    try {
      await deleteGroup(token, deleteTarget.groupId)
      setDeleteTarget(null)
      if (selectedGroup?.groupId === deleteTarget.groupId) {
        setSelectedGroup(null)
        setMembers([])
      }
      await loadGroups()
    } catch (e) {
      console.error("Failed to delete group:", e)
    }
  }

  const handleSelectGroup = (group: Group) => {
    setSelectedGroup(group)
    loadMembers(group.groupId)
  }

  const handleAddMember = async () => {
    if (!token || !selectedGroup || !selectedUserId) return
    const user = users.find((u) => u.userId === selectedUserId)
    try {
      await addGroupMember(
        token,
        selectedGroup.groupId,
        selectedUserId,
        user?.email || ""
      )
      setAddMemberOpen(false)
      setSelectedUserId("")
      await loadMembers(selectedGroup.groupId)
    } catch (e) {
      console.error("Failed to add member:", e)
    }
  }

  const handleRemoveMember = async (userId: string) => {
    if (!token || !selectedGroup) return
    try {
      await removeGroupMember(token, selectedGroup.groupId, userId)
      await loadMembers(selectedGroup.groupId)
    } catch (e) {
      console.error("Failed to remove member:", e)
    }
  }

  const openAddMember = async () => {
    if (!token) return
    try {
      setUsers(await listUsers(token))
    } catch (e) {
      console.error("Failed to load users:", e)
    }
    setSelectedUserId("")
    setAddMemberOpen(true)
  }

  // Filter out users already in the group
  const availableUsers = users.filter(
    (u) => !members.some((m) => m.userId === u.userId)
  )

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">グループ管理</h1>
        <Button onClick={openCreate}>
          <Plus className="h-4 w-4 mr-2" />
          新しいグループ
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Groups List */}
        <Card>
          <CardHeader>
            <CardTitle>グループ</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-2">
                {[...Array(3)].map((_, i) => (
                  <Skeleton key={i} className="h-16 w-full" />
                ))}
              </div>
            ) : groups.length === 0 ? (
              <p className="text-muted-foreground text-center py-4">
                グループはまだありません
              </p>
            ) : (
              <div className="space-y-2">
                {groups.map((group) => (
                  <div
                    key={group.groupId}
                    className={`flex items-center justify-between p-3 rounded-md border cursor-pointer transition-colors ${
                      selectedGroup?.groupId === group.groupId
                        ? "border-primary bg-primary/5"
                        : "hover:bg-muted/50"
                    }`}
                    onClick={() => handleSelectGroup(group)}
                  >
                    <div>
                      <div className="font-medium">{group.name}</div>
                      {group.description && (
                        <div className="text-sm text-muted-foreground">
                          {group.description}
                        </div>
                      )}
                    </div>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) => {
                          e.stopPropagation()
                          openEdit(group)
                        }}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) => {
                          e.stopPropagation()
                          setDeleteTarget(group)
                        }}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Members Panel */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>
              {selectedGroup
                ? `メンバー — ${selectedGroup.name}`
                : "グループを選択"}
            </CardTitle>
            {selectedGroup && (
              <Button size="sm" onClick={openAddMember}>
                <UserPlus className="h-4 w-4 mr-2" />
                追加
              </Button>
            )}
          </CardHeader>
          <CardContent>
            {!selectedGroup ? (
              <p className="text-muted-foreground text-center py-4">
                メンバーを表示するにはグループを選択してください
              </p>
            ) : membersLoading ? (
              <div className="space-y-2">
                {[...Array(3)].map((_, i) => (
                  <Skeleton key={i} className="h-10 w-full" />
                ))}
              </div>
            ) : members.length === 0 ? (
              <p className="text-muted-foreground text-center py-4">
                メンバーはいません
              </p>
            ) : (
              <div className="space-y-1">
                {members.map((member) => (
                  <div
                    key={member.userId}
                    className="flex items-center justify-between p-2 rounded-md hover:bg-muted/50"
                  >
                    <div>
                      <span className="text-sm">
                        {member.userEmail || member.userId}
                      </span>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleRemoveMember(member.userId)}
                    >
                      <X className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingGroup ? "グループを編集" : "グループを作成"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Input
              placeholder="グループ名"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
            />
            <Input
              placeholder="説明（任意）"
              value={groupDesc}
              onChange={(e) => setGroupDesc(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              キャンセル
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving || !groupName.trim()}
            >
              {saving ? "保存中..." : editingGroup ? "更新" : "作成"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteTarget !== null}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>グループを削除</DialogTitle>
          </DialogHeader>
          <p className="py-4">
            「{deleteTarget?.name}」を削除しますか？すべてのメンバーの関連付けが解除されます。
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>
              キャンセル
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              削除
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Member Dialog */}
      <Dialog open={addMemberOpen} onOpenChange={setAddMemberOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>メンバーを追加</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Select value={selectedUserId} onValueChange={setSelectedUserId}>
              <SelectTrigger>
                <SelectValue placeholder="ユーザーを選択" />
              </SelectTrigger>
              <SelectContent>
                {availableUsers.map((u) => (
                  <SelectItem key={u.userId} value={u.userId}>
                    {u.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddMemberOpen(false)}>
              キャンセル
            </Button>
            <Button onClick={handleAddMember} disabled={!selectedUserId}>
              追加
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
